import { useEffect, useState } from "react";

import { Button, Icon, Modal } from "semantic-ui-react";
import { css, styled, useTheme } from "styled-components";

export type ModalCarouselScreen = {
  title: string;
  content: React.ReactNode;
  actions?: ModalCarouselAction[];
  disableNext?: boolean;
};

export type ModalCarouselAction = {
  label: string;
  onClick: () => void;
  color?: string;
  icon?: string;
  primary?: boolean;
  disabled?: boolean;
  loading?: boolean;
};

type ModalCarouselProps = {
  title?: string;
  open: boolean;
  screens: ModalCarouselScreen[];
  actions?: ModalCarouselAction[];
  onClose: () => void;
  scrolling?: boolean;
};

const FlexActions = styled(Modal.Actions)`
  &&& {
    display: flex;
    align-items: center;
    padding: 1rem 1.5rem;
  }
`;

const StepProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StepCount = styled.span`
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 500;
  white-space: nowrap;
`;

const StepDots = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const stepDotStyles = css<{
  $active: boolean;
  $completed: boolean;
  $colour: string;
}>`
  border: none;
  padding: 0;
  cursor: pointer;
  transition:
    width 0.2s ease,
    background-color 0.2s ease,
    opacity 0.2s ease;
  height: 8px;
  border-radius: 4px;
  width: ${({ $active }) => ($active ? "24px" : "8px")};
  background-color: ${({ $active, $completed, $colour }) => {
    if ($active) {
      return $colour;
    }
    if ($completed) {
      return $colour;
    }
    return "rgba(0, 0, 0, 0.15)";
  }};
  opacity: ${({ $completed, $active }) => ($completed && !$active ? 0.45 : 1)};

  &:disabled {
    cursor: default;
  }

  &:not(:disabled):hover {
    opacity: 0.85;
  }
`;

const StepDot = styled.button`
  ${stepDotStyles}
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex-shrink: 0;
`;

type StepDotButtonProps = {
  isActive: boolean;
  isCompleted: boolean;
  colour: string;
  canNavigate: boolean;
  onClick: () => void;
};

const StepDotButton = ({
  isActive,
  isCompleted,
  colour,
  canNavigate,
  onClick,
}: StepDotButtonProps) => (
  <StepDot
    type="button"
    aria-label={isActive ? "Current step" : "Go to step"}
    aria-current={isActive ? "step" : undefined}
    $active={isActive}
    $completed={isCompleted}
    $colour={colour}
    disabled={!canNavigate}
    onClick={onClick}
  />
);

const ModalCarousel = ({
  title,
  open,
  onClose,
  screens,
  actions = [],
  scrolling,
}: ModalCarouselProps) => {
  const theme = useTheme();
  const [activePage, setActivePage] = useState(0);

  useEffect(() => {
    if (open) {
      setActivePage(0);
    }
  }, [open]);

  useEffect(() => {
    if (activePage >= screens.length && screens.length > 0) {
      setActivePage(screens.length - 1);
    }
  }, [activePage, screens.length]);

  const activeScreen = screens[activePage];
  const footerActions = [...actions, ...(activeScreen?.actions ?? [])];
  const stepColour = theme?.primaryColour ?? "#2185d0";
  const hasMultipleScreens = screens.length > 1;

  const nextPageAvailable = activePage < screens.length - 1;
  const prevPageAvailable = activePage !== 0;
  const nextDisabled = !nextPageAvailable || !!activeScreen?.disableNext;

  if (!screens.length) return null;
  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={open}
      onClose={onClose}
    >
      {title ?
        <Modal.Header>{title}</Modal.Header>
      : null}
      <Modal.Content scrolling={scrolling}>
        <div>
          <h1>{activeScreen?.title}</h1>
          {activeScreen?.content}
        </div>
      </Modal.Content>
      <FlexActions>
        <ActionsRow>
          <NavGroup>
            {hasMultipleScreens && prevPageAvailable ?
              <Button
                basic
                onClick={() => setActivePage((prevPage) => prevPage - 1)}
              >
                <Icon name="chevron left" />
                Back
              </Button>
            : null}
            {hasMultipleScreens ?
              <StepProgress>
                <StepCount>
                  {activePage + 1} of {screens.length}
                </StepCount>
                <StepDots>
                  {screens.map((screen, index) => (
                    <StepDotButton
                      key={`${screen.title}-${index}`}
                      isActive={activePage === index}
                      isCompleted={index < activePage}
                      colour={stepColour}
                      canNavigate={index <= activePage}
                      onClick={() => {
                        if (index <= activePage) {
                          setActivePage(index);
                        }
                      }}
                    />
                  ))}
                </StepDots>
              </StepProgress>
            : null}
          </NavGroup>

          <ActionGroup>
            {footerActions.map((action) => (
              <Button
                key={action.label}
                primary={action.primary}
                content={action.label}
                icon={action.icon}
                disabled={action.disabled}
                loading={action.loading}
                onClick={action.onClick}
              />
            ))}
            {hasMultipleScreens && nextPageAvailable ?
              <Button
                primary
                disabled={nextDisabled}
                onClick={() => setActivePage((prevPage) => prevPage + 1)}
              >
                Next
                <Icon name="chevron right" />
              </Button>
            : null}
          </ActionGroup>
        </ActionsRow>
      </FlexActions>
    </Modal>
  );
};

export default ModalCarousel;
